import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  LoadingSpinner,
  DataLoadingWrapper,
  MultiSectionLayout,
} from "@jugl-web/ui-kit";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useUpdateEmailSettings } from "../../hooks/useUpdateEmailSettings";
import { AIEmailInstructions } from "../AIEmailInstructions";
import { AIEmailSignatureSection } from "../AIEmailSignatureSection";
import { AIMessageDisclaimerSection } from "../AIMessageDisclaimerSection";
import { BlockedContactsForAISection } from "../BlockedContactsForAISection";
import { ResponseModeSection } from "../ResponseModeSection";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { getEmailSettingsFormValues } from "./utils/getEmailSettingsFormValues";
import { EmailSettingsFormValues } from "./types";

export const EmailSettingsTab: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { emailAccount } = useEmailAccount();
  const { updateEmailSettings } = useUpdateEmailSettings();
  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();
  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();
  const { integrationsApi } = useRestApiProvider();
  const {
    isLoading: isAgentConfigLoading,
    isError: isAgentConfigError,
    refetch: refetchAgentConfig,
  } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });

  const form = useForm<EmailSettingsFormValues>({
    defaultValues: getEmailSettingsFormValues(emailAccount),
    reValidateMode: "onBlur",
  });

  const { handleSubmit, reset, formState } = form;

  const resetForm = useCallback(() => {
    reset(getEmailSettingsFormValues(emailAccount));
  }, [emailAccount, reset]);

  const handleSaveChanges = useCallback(
    async (formData: EmailSettingsFormValues) => {
      try {
        setIsSubmitting(true);

        const isUpdated = await updateEmailSettings({
          ai_reply: formData.ai_reply,
          metadata: {
            use_base_instructions: formData.use_base_instructions,
            instructions: formData.instructions.trim(),
            ai_email_signature_enabled: formData.ai_email_signature_enabled,
            ai_email_signature: formData.ai_email_signature.trim(),
            ai_msg_disclaimer_enabled: formData.ai_msg_disclaimer_enabled,
            ai_msg_disclaimer: formData.ai_msg_disclaimer.trim(),
            blocked_emails: formData.blocked_emails,
          },
        });

        if (isUpdated) {
          reset(formData);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [reset, setIsSubmitting, updateEmailSettings]
  );

  useEffect(() => {
    return registerDirtySource("email-settings-tab", formState.isDirty);
  }, [formState.isDirty, registerDirtySource]);

  return (
    <FormProvider {...form}>
      <MultiSectionLayout
        className="h-full"
        classNames={{
          content: "overflow-y-auto",
        }}
        footer={
          formState.isDirty ? (
            <div className="flex gap-4 border-0 border-t border-solid border-dark-100 bg-white px-8 py-4 max-lg:px-4">
              <Button
                variant="tertiary"
                size="md"
                className="w-30"
                onClick={() => requestNavigation(resetForm)}
                isDisabled={isSubmitting}
              >
                {t({
                  id: "common.cancel",
                  defaultMessage: "Cancel",
                })}
              </Button>
              <Button
                variant="primary"
                size="md"
                className="w-75"
                onClick={handleSubmit(handleSaveChanges)}
                isDisabled={isSubmitting}
                iconEnd={
                  isSubmitting ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <ArrowRightIcon />
                  )
                }
              >
                {t({
                  id: "common.save-changes",
                  defaultMessage: "Save changes",
                })}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 p-8 max-lg:gap-4 max-lg:p-4">
          <DataLoadingWrapper
            isLoading={isAgentConfigLoading}
            isError={isAgentConfigError}
            onRetry={refetchAgentConfig}
          >
            <ResponseModeSection />
            <AIEmailInstructions />
            <AIEmailSignatureSection />
            <AIMessageDisclaimerSection />
            <BlockedContactsForAISection />
          </DataLoadingWrapper>
        </div>
      </MultiSectionLayout>
    </FormProvider>
  );
};
